import type {
    ContractToRegistrationProcessResponse,
    IContractToRegistrationProcessActivity,
    IContractToRegistrationProcessResponse,
} from './classes/ContractToRegistrationProcessResponse';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
    createSlice,
    isAnyOf,
    createAction,
    createAsyncThunk,
} from '@reduxjs/toolkit';
import { contractToRegistrationApi } from './services/rtkApi';
import { formGeneratorApi } from '@klp/formgenerator/services/rtkApi';

export interface IContractToRegistrationProcessSlice extends IContractToRegistrationProcessResponse {
    formFieldProcessing: boolean;
    formFieldError: boolean;
}

export type IContractToRegistrationProcessState =
    IContractToRegistrationProcessSlice;

export type IContractToRegistrationProcessStore = {
    contractToRegistrationProcess: IContractToRegistrationProcessState;
};

export const contractregistrationSelectors = ({
    contractToRegistrationProcess,
}: IContractToRegistrationProcessStore): IContractToRegistrationProcessSlice =>
    contractToRegistrationProcess;

export const contractToRegistrationProcessFetched = createAction(
    'contractToRegistrationProcess/InitialResponseFetched',
    (data: ContractToRegistrationProcessResponse) => ({
        payload: data,
    }),
);

const matchContractToRegistrationProcessFetched = isAnyOf(
    contractToRegistrationProcessFetched,
    contractToRegistrationApi.endpoints.getContractRegistrationProcess
        .matchFulfilled,
);

export const contractToRegistrationformFieldProcessing = createAction(
    'contractToRegistrationProcess/FormfieldSaved',
);

export const matchContractToRegistrationformFieldProcessing = isAnyOf(
    contractToRegistrationformFieldProcessing,
    formGeneratorApi.endpoints.saveKontraktsforvaltningProcessFormfields
        .matchFulfilled,
    formGeneratorApi.endpoints.saveKontraktsforvaltningProcessFormfields
        .matchPending,
    formGeneratorApi.endpoints.saveKontraktsforvaltningProcessFormfields
        .matchRejected,
);

export const setAndResetformFieldProcessing = createAsyncThunk(
    'contractToRegistrationProcess/setAndResetformFieldProcessing',
    async (_, { dispatch }) => {
        dispatch(setformFieldProcessing());
        await new Promise((resolve) => setTimeout(resolve, 1500));
        dispatch(resetformFieldProcessing());
    },
);

const initialState: IContractToRegistrationProcessSlice = {
    formFieldProcessing: false,
    formFieldError: false,
    prosessDefinisjonId: undefined,
    kontraktDokumentId: undefined,
    registreringStatus: undefined,
    ansvarligForvalterId: undefined,
    ansvarligOekonomiId: undefined,
    ansvarligOekonomiQAId: undefined,
    id: '',
    navn: undefined,
    ansvarlig: '',
    startDato: undefined,
    sluttDato: undefined,
    fullfortDato: undefined,
    startetAvBruker: undefined,
    type: 'Kontrakt',
    aktiviteter: [],
    currentAktivitet: [],
};
// implement this using createEntityAdapter

export const contractToRegistrationProcessSelectors = ({
    contractToRegistrationProcess,
}: IContractToRegistrationProcessStore): IContractToRegistrationProcessState =>
    contractToRegistrationProcess;

const contractToRegistrationProcessSlice = createSlice({
    name: 'contractToRegistrationProcess',
    initialState: initialState,
    reducers: {
        updateProcess: (
            state,
            action: PayloadAction<ContractToRegistrationProcessResponse>,
        ) => {
            return {
                ...action.payload,
                formFieldProcessing: state.formFieldProcessing,
                formFieldError: state.formFieldError,
            };
        },
        updateActivity: (
            state,
            action: PayloadAction<IContractToRegistrationProcessActivity>,
        ) => {
            const updatedActivity = action.payload;

            if (state.aktiviteter) {
                const activityIndex = state.aktiviteter.findIndex(
                    (activity) => activity.id === updatedActivity.id,
                );
                if (activityIndex !== -1) {
                    state.aktiviteter[activityIndex] = updatedActivity;
                }

                const currentActivityIndex = state.currentAktivitet.findIndex(
                    (activity) => activity.id === updatedActivity.id,
                );
                if (currentActivityIndex !== -1) {
                    state.currentAktivitet[currentActivityIndex] =
                        updatedActivity;
                }

                return state;
            }

            return state;
        },
        setformFieldProcessing(state) {
            state.formFieldProcessing = true;
        },
        resetformFieldProcessing(state) {
            state.formFieldProcessing = false;
        },
        setFormFieldErrors(state, action) {
            state.formFieldError = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            matchContractToRegistrationProcessFetched,
            (state, action) => {
                Object.keys(initialState).forEach((key) => {
                    state[key] = action.payload[key];
                });
            },
        );
    },
});

export const {
    updateProcess,
    updateActivity,
    setformFieldProcessing,
    resetformFieldProcessing,
    setFormFieldErrors,
} = contractToRegistrationProcessSlice.actions;

export const contractregistrationActions = {
    reducerActions: contractToRegistrationProcessSlice.actions,
};
export default contractToRegistrationProcessSlice;
